import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Clock, Check, X } from 'lucide-react';
import { species as allSpecies } from '@/data';

interface MatchCard {
  id: string;
  type: 'species' | 'habitat';
  value: string;
  displayText: string;
  emoji: string;
  matched: boolean;
  flipped: boolean;
  matchId: string;
}

interface HabitatMatchingGameProps {
  onComplete: (score: number, stars: number) => void;
  onBack: () => void;
  speciesId: string;
  otterName: string;
}

const HABITAT_EMOJIS: Record<string, string> = {
  'kelp forests': '🌿',
  'rivers': '🏞️',
  'lakes': '🏝️',
  'coastal areas': '🏖️',
  'swamps': '🌾',
  'mangroves': '🌴',
  'marshes': '🦢',
  'streams': '💧',
  'wetlands': '🐸',
  'rainforest': '🌳',
  'ocean': '🌊',
};

function createCards(): MatchCard[] {
  // Select 6 random species for the matching game
  const selectedSpecies = [...allSpecies]
    .sort(() => Math.random() - 0.5)
    .slice(0, 6);

  const cards: MatchCard[] = [];

  selectedSpecies.forEach((species, index) => {
    const matchId = `match-${index}`;
    const primaryHabitat = species.habitat.environment[0].toLowerCase();
    const habitatEmoji = Object.entries(HABITAT_EMOJIS).find(([key]) =>
      primaryHabitat.includes(key)
    )?.[1] || '🌍';

    // Species card
    cards.push({
      id: `species-${index}`,
      type: 'species',
      value: species.id,
      displayText: species.commonName,
      emoji: '🦦',
      matched: false,
      flipped: false,
      matchId,
    });

    // Habitat card
    cards.push({
      id: `habitat-${index}`,
      type: 'habitat',
      value: primaryHabitat,
      displayText: species.habitat.environment[0],
      emoji: habitatEmoji,
      matched: false,
      flipped: false,
      matchId,
    });
  });

  // Shuffle cards
  return cards.sort(() => Math.random() - 0.5);
}

export function HabitatMatchingGame({ onComplete, onBack, speciesId, otterName }: HabitatMatchingGameProps) {
  const [cards, setCards] = useState<MatchCard[]>(() => createCards());
  const [flippedCards, setFlippedCards] = useState<string[]>([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [moves, setMoves] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isComplete, setIsComplete] = useState(false);
  const [lastResult, setLastResult] = useState<'match' | 'miss' | null>(null);

  const totalPairs = cards.length / 2;

  // Timer
  useEffect(() => {
    if (isComplete || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setIsComplete(true);
          const stars = matchedPairs >= totalPairs ? 3 : matchedPairs >= totalPairs / 2 ? 1 : 0;
          setTimeout(() => onComplete(matchedPairs * 100, stars), 1500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isComplete, timeLeft, matchedPairs, totalPairs, onComplete]);

  // Check for matches
  useEffect(() => {
    if (flippedCards.length === 2) {
      const [first, second] = flippedCards;
      const firstCard = cards.find(c => c.id === first);
      const secondCard = cards.find(c => c.id === second);

      if (firstCard && secondCard) {
        setMoves(m => m + 1);

        if (firstCard.matchId === secondCard.matchId) {
          // Match!
          setLastResult('match');
          setTimeout(() => {
            setCards(prev => prev.map(c =>
              c.id === first || c.id === second ? { ...c, matched: true } : c
            ));
            setMatchedPairs(p => {
              const newPairs = p + 1;
              if (newPairs >= totalPairs) {
                setIsComplete(true);
                const stars = timeLeft > 30 ? 3 : timeLeft > 15 ? 2 : 1;
                setTimeout(() => onComplete(newPairs * 100 + timeLeft * 5, stars), 1500);
              }
              return newPairs;
            });
            setFlippedCards([]);
            setLastResult(null);
          }, 500);
        } else {
          // No match
          setLastResult('miss');
          setTimeout(() => {
            setCards(prev => prev.map(c =>
              c.id === first || c.id === second ? { ...c, flipped: false } : c
            ));
            setFlippedCards([]);
            setLastResult(null);
          }, 1000);
        }
      }
    }
  }, [flippedCards, cards, totalPairs, timeLeft, onComplete]);

  const handleCardClick = useCallback((card: MatchCard) => {
    if (isComplete || card.matched || card.flipped || flippedCards.length >= 2) return;

    setCards(prev => prev.map(c =>
      c.id === card.id ? { ...c, flipped: true } : c
    ));
    setFlippedCards(prev => [...prev, card.id]);
  }, [isComplete, flippedCards]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-emerald-900 to-emerald-950 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onBack} className="text-white/60 text-sm">
            ← Back
          </button>
          <h2 className="text-white font-bold">Habitat Matching</h2>
          <div className="flex items-center gap-1 text-white">
            <Clock className="w-4 h-4" />
            <span className={`font-bold ${timeLeft <= 10 ? 'text-red-400' : ''}`}>
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between text-sm">
          <div className="text-white/60">
            Pairs: <span className="text-white font-bold">{matchedPairs}/{totalPairs}</span>
          </div>
          <div className="text-white/60">
            Moves: <span className="text-white font-bold">{moves}</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="px-4 py-2 bg-emerald-800/30">
        <p className="text-emerald-200 text-sm text-center">
          Match each otter species with their habitat!
        </p>
      </div>

      {/* Game grid */}
      <div className="flex-1 p-4 flex items-center justify-center">
        <div
          className="grid gap-2 w-full max-w-md"
          style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
        >
          {cards.map(card => (
            <motion.button
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={`
                aspect-square rounded-xl flex flex-col items-center justify-center p-1
                transition-all duration-200
                ${card.matched
                  ? 'bg-green-600 opacity-50'
                  : card.flipped
                    ? 'bg-white'
                    : 'bg-emerald-700 hover:bg-emerald-600'
                }
              `}
              animate={{
                rotateY: card.flipped || card.matched ? 180 : 0,
                scale: card.matched ? 0.9 : 1,
              }}
              transition={{ duration: 0.3 }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              {(card.flipped || card.matched) ? (
                <div className="text-center" style={{ transform: 'rotateY(180deg)' }}>
                  <div className="text-2xl mb-0.5">{card.emoji}</div>
                  <div className={`text-xs font-medium leading-tight ${
                    card.matched ? 'text-white' : 'text-gray-800'
                  }`}>
                    {card.displayText.split(' ').slice(0, 2).join(' ')}
                  </div>
                </div>
              ) : (
                <div className="text-3xl">❓</div>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Match/Miss feedback */}
      <AnimatePresence>
        {lastResult && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className={`
              absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              px-6 py-3 rounded-full font-bold text-lg pointer-events-none
              ${lastResult === 'match' ? 'bg-green-500' : 'bg-red-500'}
            `}
          >
            {lastResult === 'match' ? (
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5" /> Match!
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <X className="w-5 h-5" /> Try Again
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion overlay */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/70 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`rounded-2xl p-6 text-center ${
                matchedPairs >= totalPairs ? 'bg-green-700' : 'bg-orange-700'
              }`}
            >
              <h3 className="text-2xl font-bold text-white mb-2">
                {matchedPairs >= totalPairs ? 'Perfect Match!' : 'Time\'s Up!'}
              </h3>
              <p className="text-white/80 mb-2">
                {matchedPairs >= totalPairs
                  ? `All habitats matched in ${moves} moves!`
                  : `Matched ${matchedPairs}/${totalPairs} pairs`
                }
              </p>
              <div className="text-2xl font-bold text-yellow-400 mb-2">
                {matchedPairs * 100 + (matchedPairs >= totalPairs ? timeLeft * 5 : 0)} points
              </div>
              <div className="flex justify-center gap-1">
                {[1, 2, 3].map(i => (
                  <Star
                    key={i}
                    className={`w-8 h-8 ${
                      matchedPairs >= totalPairs
                        ? (timeLeft > 30 && i <= 3) || (timeLeft > 15 && i <= 2) || i <= 1
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-400'
                        : matchedPairs >= totalPairs / 2 && i === 1
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-400'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tips */}
      <div className="p-4 bg-black/30">
        <p className="text-white/50 text-xs text-center">
          Tap cards to flip them. Match species with their primary habitat!
        </p>
      </div>
    </div>
  );
}
